import { useState } from "react";
import { useSelector } from "react-redux";
import { UsersThree, Calendar } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectIsAdmin,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { PageHeader } from "../../components/shared";
import { PermissionRoute } from "../../components/shared/access";
import ClinicCalendarModal from "../../components/calendar/ClinicCalendarModal";
import TeamCoverageGrid from "../../components/calendar/coverage/TeamCoverageGrid";
import "./ClinicCalendarPage.css";

/**
 * ClinicCalendarPage — `/dashboard/clinic-calendar` (multi-clinic v2).
 *
 * The clinic-wide calendar as a sidebar page for staff who hold
 * `calendar.viewClinic` (managers, secretaries, therapists the owner opened
 * it for). Admins always pass. Scheduling on behalf of others inside it is
 * governed by `calendar.manageOthersAppointments` (handled by the embedded
 * ClinicCalendarModal — read-only banner otherwise).
 *
 * The route itself is registered in App.js; this file only exports the page.
 */
function ClinicCalendarPage() {
  const clinicId = useSelector(selectClinicId);
  const isAdmin = useSelector(selectIsAdmin);
  const canEditOthersAvailability = useSelector(
    selectHasPermission("calendar.editOthersAvailability")
  );
  // Tabs: "calendar" (embedded clinic calendar) | "coverage" (team availability grid)
  const [activeTab, setActiveTab] = useState("calendar");

  return (
    <div className="clinic-calendar-page">
      <PageHeader
        icon={UsersThree}
        title="יומן הקליניקה"
        subtitle="כל הפגישות של הצוות ביומן אחד"
      />
      <PermissionRoute perm="calendar.viewClinic">
        {/* Tabs — flat, flush, full-width (design 2026 §3) */}
        <div className="clinic-calendar-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "calendar"}
            className={`clinic-calendar-tab ${activeTab === "calendar" ? "active" : ""}`}
            onClick={() => setActiveTab("calendar")}
          >
            <Calendar size={15} weight={activeTab === "calendar" ? "fill" : "regular"} />
            <span>יומן</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "coverage"}
            className={`clinic-calendar-tab ${activeTab === "coverage" ? "active" : ""}`}
            onClick={() => setActiveTab("coverage")}
          >
            <UsersThree size={15} weight={activeTab === "coverage" ? "fill" : "regular"} />
            <span>זמינות הצוות</span>
          </button>
        </div>

        {/* The embedded calendar keeps un-lifted state (date/view) — stay mounted, hide via class */}
        <div
          className={`clinic-calendar-panel ${activeTab === "calendar" ? "" : "clinic-calendar-panel-hidden"}`}
          aria-hidden={activeTab !== "calendar"}
        >
          <ClinicCalendarModal embedded />
        </div>

        {activeTab === "coverage" && (
          <TeamCoverageGrid
            clinicId={clinicId}
            canEditAvailability={isAdmin || canEditOthersAvailability}
            showTherapistNames
          />
        )}
      </PermissionRoute>
    </div>
  );
}

export default ClinicCalendarPage;
